/**
 * Transport Layer — WebSocket + REST Communication
 *
 * Primary WebSocket transport with automatic REST fallback.
 * Handles reconnection with exponential backoff, heartbeat keep-alive,
 * message queuing during disconnection, and automatic upgrade back
 * to WebSocket when available.
 *
 * Session tokens are held in memory only (never localStorage) per PDPA/M07-T05.
 *
 * @module M01-T02
 */

import { clearTicket, readTicket, storeTicket } from "./resumeTicket";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Connection state of the transport layer */
export enum TransportState {
  CONNECTING = "CONNECTING",
  CONNECTED_WS = "CONNECTED_WS",
  CONNECTED_REST = "CONNECTED_REST",
  RECONNECTING = "RECONNECTING",
  DISCONNECTED = "DISCONNECTED",
  ERROR = "ERROR",
}

/** Wire-format message exchanged with the backend */
export interface ChatMessage {
  type: string;
  payload: Record<string, unknown>;
  message_id: string;
  timestamp: number;
  metadata?: Record<string, unknown>;
}

/** Callback invoked when a message arrives from the server */
export type MessageHandler = (message: ChatMessage) => void;

/** Callback invoked when the transport state changes */
export type StateChangeHandler = (state: TransportState) => void;

/** Configuration for the transport layer */
export interface TransportConfig {
  /** WebSocket URL (e.g. wss://api.hana.hmi.sg/ws/chat) */
  wsUrl: string;
  /** REST fallback URL (e.g. https://api.hana.hmi.sg) */
  restUrl: string;
  /** Entity identifier passed as query param */
  entityId: string;
  /** Session token — held in memory only, never persisted to storage */
  sessionToken?: string;
  /**
   * Acceptance-harness binding ID — public, non-secret UUID4 hex (32
   * chars). When set, the WebSocket includes it as a query param so the
   * backend chat handler can publish trace events back to the admin's
   * SSE stream. Plain production widget mounts (HMI public site) MUST
   * omit this. See workspaces/hana-p2/journal/0094.
   */
  harnessSessionId?: string;
  /**
   * Test-harness sandbox mode flag. When true, every outbound REST
   * call (session create, message send, message poll) carries the
   * `X-Hana-Env: sandbox` header per spec
   * `specs/acceptance-test-harness.md` §4.2 SS-2.
   * Production end-user mounts MUST omit this.
   */
  testHarness?: boolean;
  /** Maximum reconnection attempts before falling back to REST (default 5) */
  reconnectMaxAttempts?: number;
  /** Base delay in ms for exponential backoff (default 1000) */
  reconnectBaseDelay?: number;
  /** Heartbeat interval in ms (default 30000) */
  heartbeatInterval?: number;
  /** REST polling interval in ms (default 3000) */
  restPollInterval?: number;
}

// ---------------------------------------------------------------------------
// Internal defaults
// ---------------------------------------------------------------------------

const DEFAULT_RECONNECT_MAX_ATTEMPTS = 5;
const DEFAULT_RECONNECT_BASE_DELAY = 1000;
/** Maximum delay between reconnect attempts (Brief Section 3.2E: max 30s) */
const MAX_RECONNECT_DELAY = 30_000;
const DEFAULT_HEARTBEAT_INTERVAL = 30_000;
const DEFAULT_REST_POLL_INTERVAL = 3000;

// ---------------------------------------------------------------------------
// REST transport (internal)
// ---------------------------------------------------------------------------

/**
 * RESTTransport manages session creation, message sending, and long-polling
 * via the backend REST API. It is an internal class used by TransportLayer
 * when WebSocket is unavailable.
 */
export class RESTTransport {
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _lastTimestamp = 0;
  private _sessionId: string | null = null;
  private readonly _config: Required<
    Pick<TransportConfig, "restUrl" | "entityId" | "restPollInterval">
  > & { sessionToken?: string };
  private _onMessage: MessageHandler | null = null;
  private _onSessionToken: ((token: string) => void) | null = null;
  private _active = false;
  private readonly _testHarness: boolean;

  constructor(config: TransportConfig) {
    this._config = {
      restUrl: config.restUrl,
      entityId: config.entityId,
      restPollInterval: config.restPollInterval ?? DEFAULT_REST_POLL_INTERVAL,
      sessionToken: config.sessionToken,
    };
    this._testHarness = config.testHarness === true;
  }

  // -- public API ----------------------------------------------------------

  /** Update the session token (in-memory only) */
  setSessionToken(token: string): void {
    this._config.sessionToken = token;
  }

  /** Register the message handler for polled messages */
  onMessage(handler: MessageHandler): void {
    this._onMessage = handler;
  }

  /**
   * Register a handler invoked when the backend issues (or confirms) a
   * session handle during REST connect. GH #753 — TransportLayer uses
   * this to keep ITS copy of the token in sync, so a later WS upgrade
   * handshakes with the SAME session instead of minting a new one.
   */
  onSessionToken(handler: (token: string) => void): void {
    this._onSessionToken = handler;
  }

  /**
   * Adopt a session payload returned by create OR resume.
   *
   * Both endpoints return the same credential shape, so the handling is
   * shared — including storing the rolling resume ticket (REQ-A-020).
   */
  private _adoptSession(data: {
    session_id: string;
    session_handle?: string;
    token?: string;
    resume_ticket?: { value?: string };
  }): void {
    this._sessionId = data.session_id;
    const handle = data.session_handle ?? data.token;
    if (handle) {
      this._config.sessionToken = handle;
      this._onSessionToken?.(handle);
    }
    // #880 — the ticket rolls on every session-bearing response, so the
    // stored value is never older than the last turn. The TOKEN is still
    // never written to storage (M07-T05).
    const ticket = data.resume_ticket?.value;
    if (ticket) storeTicket(this._config.entityId, ticket);
  }

  /**
   * Try to re-establish the prior session from a stored resume ticket.
   *
   * Returns the replayed transcript on success, or null — and null is the
   * ONLY thing the caller needs to distinguish. Unknown ticket, expired
   * ticket, entity mismatch, dead session and losing the burn race to a
   * sibling tab all return an identical 401 by design; the widget treats
   * every one of them by minting a fresh session, silently, with no error
   * shown to the patient.
   */
  private async _tryResume(): Promise<
    Array<{ role: string; text: string; timestamp?: number }> | null
  > {
    // If a session token is already in hand, someone above us has already
    // established the session — TransportLayer resumes before it connects,
    // and then falls back here when the WebSocket fails. Re-attempting would
    // consume the ticket TransportLayer had just stored and burn it for
    // nothing, leaving the next navigation with no ticket at all.
    if (this._config.sessionToken) return null;

    const ticket = readTicket(this._config.entityId);
    if (!ticket) return null;
    // Spend it locally BEFORE the request. The server burns it on receipt
    // whatever the outcome, so retaining it would only make the next
    // navigation retry a value that is already dead.
    clearTicket(this._config.entityId);
    try {
      const res = await fetch(`${this._config.restUrl}/api/chat/session/resume`, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({
          ticket,
          entity_id: this._config.entityId,
        }),
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        session_id: string;
        session_handle?: string;
        resume_ticket?: { value?: string };
        messages?: Array<{ role: string; text: string; timestamp?: number }>;
      };
      if (!data?.session_id) return null;
      this._adoptSession(data);
      return Array.isArray(data.messages) ? data.messages : [];
    } catch {
      // Network failure is indistinguishable from refusal, and both mean
      // the same thing to the patient: start a fresh conversation.
      return null;
    }
  }

  /**
   * Create a session via POST /api/chat/session and start polling.
   *
   * #880: a stored resume ticket is tried FIRST, so a document load on a
   * non-SPA entity site continues the conversation instead of minting a
   * fresh session and re-asking for Terms of Use acceptance.
   */
  async connect(): Promise<void> {
    const replayed = await this._tryResume();
    if (replayed !== null) {
      this._active = true;
      // Advance the poll watermark PAST the replayed turns BEFORE polling
      // starts. The REST buffer is keyed by session_id — which resume
      // deliberately keeps unchanged — and `since()` filters rather than
      // drains, so with _lastTimestamp still 0 the very next poll re-sends
      // every retained frame and the patient sees each bot turn twice.
      // Both sides are epoch seconds (protocol.py WSMessage.timestamp and
      // dialogue_manager's conversation_history both use time.time()).
      for (const turn of replayed) {
        const ts = turn.timestamp ?? 0;
        if (ts > this._lastTimestamp) this._lastTimestamp = ts;
      }
      this._startPolling();
      // Render the prior turns through the normal message path so they
      // are indistinguishable from live ones to the UI layer.
      for (const turn of replayed) {
        this._onMessage?.({
          type: turn.role === "user" ? "user_message" : "bot_message",
          payload: { text: turn.text, replayed: true },
          message_id: `replay-${turn.timestamp ?? 0}-${Math.random().toString(36).slice(2)}`,
          timestamp: turn.timestamp ?? 0,
          metadata: { replayed: true },
        });
      }
      return;
    }

    const res = await fetch(`${this._config.restUrl}/api/chat/session`, {
      method: "POST",
      headers: this._headers(),
      body: JSON.stringify({ entity_id: this._config.entityId }),
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(`REST session creation failed: ${res.status}`);
    }
    const data = (await res.json()) as {
      session_id: string;
      session_handle?: string;
      // #880 — rolling single-use resume ticket (REQ-A-020).
      resume_ticket?: { value?: string; expires_in?: number };
      // `token` is a one-cycle compatibility alias for `session_handle`.
      // Remove after the server stops emitting it. See
      // specs/widget-auth-contract.md § 1.
      token?: string;
    };
    this._adoptSession(data);
    // Prefer session_handle (the canonical field per
    // specs/widget-auth-contract.md § 1); fall back to `token` during
    // the migration window. Once populated, _headers() (below) stamps
    // `Authorization: Bearer <handle>` on every subsequent REST call,
    // which is the cross-origin credential path required when Safari
    // ITP / Chrome 3PCD / Firefox ETP blocks the third-party cookie.
    // Per PDPA/M07-T05 the handle stays in memory only — NEVER persist
    // to localStorage / sessionStorage / IndexedDB.
    // (credential adoption + ticket storage happen in _adoptSession above;
    // GH #753 — the handle is surfaced to TransportLayer there so a later
    // WS upgrade resumes this same session.)
    this._active = true;
    this._startPolling();
  }

  /** Send a message via POST /api/chat/message */
  async send(message: ChatMessage): Promise<void> {
    const res = await fetch(`${this._config.restUrl}/api/chat/message`, {
      method: "POST",
      headers: this._headers(),
      body: JSON.stringify({
        session_id: this._sessionId,
        ...message,
      }),
      credentials: "include",
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      // The server mints a fresh resume ticket on EVERY reply. Without
      // reading it here the widget kept whatever it got at connect time,
      // so a patient who chatted for minutes before clicking still carried
      // a stale ticket — the exact staleness the rolling mint exists to fix.
      const rolled = (body as { resume_ticket?: { value?: string } })
        ?.resume_ticket?.value;
      if (rolled) storeTicket(this._config.entityId, rolled);
    }
    if (!res.ok) {
      // GH #792: 409 with whatsapp_transitioned means the session
      // has moved to WhatsApp — surface the transition to the caller.
      if (res.status === 409 && body.whatsapp_transitioned) {
        const err = new Error(body.message || "Session moved to WhatsApp");
        (err as any).whatsappTransitioned = true;
        throw err;
      }
      throw new Error(`REST send failed: ${res.status}`);
    }
    // GH #792: server may create a new session after WhatsApp
    // transition. Switch to it so subsequent messages go to the
    // new case.
    if (body.new_session_token) {
      this._config.sessionToken = body.new_session_token;
      this._onSessionToken?.(body.new_session_token);
    }
  }

  /** Stop polling and release resources */
  disconnect(): void {
    this._active = false;
    this._stopPolling();
    this._sessionId = null;
  }

  /** Whether the REST transport is currently connected */
  get active(): boolean {
    return this._active;
  }

  // -- internals -----------------------------------------------------------

  private _headers(): Record<string, string> {
    const h: Record<string, string> = { "Content-Type": "application/json" };
    if (this._config.sessionToken) {
      h["Authorization"] = `Bearer ${this._config.sessionToken}`;
    }
    // Spec §4.2 SS-2 — sandbox env propagation. Set ONLY when the
    // widget is mounted with `test-harness="1"`; production end-user
    // mounts omit the header so backend defaults to production env.
    if (this._testHarness) {
      h["X-Hana-Env"] = "sandbox";
    }
    return h;
  }

  private _startPolling(): void {
    this._stopPolling();
    this._pollTimer = setInterval(() => {
      void this._poll();
    }, this._config.restPollInterval);
  }

  private _stopPolling(): void {
    if (this._pollTimer !== null) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private async _poll(): Promise<void> {
    if (!this._active || !this._sessionId) return;
    try {
      const url = new URL(`${this._config.restUrl}/api/chat/messages`);
      url.searchParams.set("session_id", this._sessionId);
      url.searchParams.set("since", String(this._lastTimestamp));

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: this._headers(),
        credentials: "include",
      });
      if (!res.ok) return;

      const data = (await res.json()) as { messages: ChatMessage[] };
      if (data.messages && Array.isArray(data.messages)) {
        for (const msg of data.messages) {
          if (msg.timestamp > this._lastTimestamp) {
            this._lastTimestamp = msg.timestamp;
          }
          this._onMessage?.(msg);
        }
      }
    } catch {
      // Polling failures are non-fatal — next cycle retries
    }
  }
}

// ---------------------------------------------------------------------------
// TransportLayer (primary public class)
// ---------------------------------------------------------------------------

/**
 * TransportLayer provides a unified send/receive interface over WebSocket
 * with automatic REST fallback. It manages reconnection, heartbeat, and
 * message queuing transparently.
 *
 * Usage:
 * ```ts
 * const transport = new TransportLayer(config);
 * transport.onMessage((msg) => console.log(msg));
 * transport.onStateChange((state) => updateUI(state));
 * await transport.connect();
 * transport.send({ type: "user_message", payload: { text: "Hello" }, ... });
 * ```
 */
export class TransportLayer {
  private _state: TransportState = TransportState.DISCONNECTED;
  private _ws: WebSocket | null = null;
  private _rest: RESTTransport;
  /**
   * REQ-A-020 / #880 — this layer painted the transcript from a ticket
   * resume, so the handshake must not replay the bot frames again.
   *
   * Sent to the server rather than filtered here on arrival. The first
   * attempt WAS a client-side timestamp watermark and it did not hold: the
   * buffered frame and the history turn it duplicates are stamped at
   * different moments of the same reply, so the frame can be a fraction of
   * a second NEWER than the turn, and `<=` lets exactly those through. Seen
   * live: watermark 1787575509.5219672, duplicate still rendered. Frame
   * KIND does not race; wall-clock ordering between two writers does.
   */
  private _replayedTranscript = false;
  private _messageHandlers: MessageHandler[] = [];
  private _stateHandlers: StateChangeHandler[] = [];
  private _reconnectAttempt = 0;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _upgradeTimer: ReturnType<typeof setInterval> | null = null;
  private _messageQueue: ChatMessage[] = [];
  private _disposed = false;

  private readonly _config: Required<
    Pick<
      TransportConfig,
      | "wsUrl"
      | "restUrl"
      | "entityId"
      | "reconnectMaxAttempts"
      | "reconnectBaseDelay"
      | "heartbeatInterval"
      | "restPollInterval"
    >
  > & { sessionToken?: string; harnessSessionId?: string };

  constructor(config: TransportConfig) {
    this._config = {
      wsUrl: config.wsUrl,
      restUrl: config.restUrl,
      entityId: config.entityId,
      sessionToken: config.sessionToken,
      harnessSessionId: config.harnessSessionId,
      reconnectMaxAttempts:
        config.reconnectMaxAttempts ?? DEFAULT_RECONNECT_MAX_ATTEMPTS,
      reconnectBaseDelay:
        config.reconnectBaseDelay ?? DEFAULT_RECONNECT_BASE_DELAY,
      heartbeatInterval: config.heartbeatInterval ?? DEFAULT_HEARTBEAT_INTERVAL,
      restPollInterval: config.restPollInterval ?? DEFAULT_REST_POLL_INTERVAL,
    };

    this._rest = new RESTTransport(config);
    this._rest.onMessage((msg) => this._dispatchMessage(msg));
    // GH #753 — when REST connect mints/confirms a session handle, sync
    // it into this layer's config so a subsequent WS upgrade resumes
    // the SAME session (setSessionToken also writes it back to _rest,
    // which is a harmless idempotent set).
    this._rest.onSessionToken((token) => this.setSessionToken(token));
  }

  // -- public API ----------------------------------------------------------

  /** Current transport state */
  get state(): TransportState {
    return this._state;
  }

  /** Read-only access to the resolved configuration */
  get config(): Readonly<TransportConfig> {
    return { ...this._config };
  }

  /** Update the session token (in-memory only — never persisted to storage) */
  setSessionToken(token: string): void {
    this._config.sessionToken = token;
    this._rest.setSessionToken(token);
  }

  /** Register a handler invoked for every incoming server message */
  onMessage(handler: MessageHandler): void {
    this._messageHandlers.push(handler);
  }

  /** Register a handler invoked when the transport state changes */
  onStateChange(handler: StateChangeHandler): void {
    this._stateHandlers.push(handler);
  }

  /**
   * Connect to the backend. Tries WebSocket first; on failure, falls
   * back to REST polling automatically.
   */
  async connect(): Promise<void> {
    if (this._disposed) return;
    this._setState(TransportState.CONNECTING);

    // REQ-A-020 / #880 — exchange a stored resume ticket BEFORE the
    // handshake, whichever transport we end up on.
    //
    // The WS handshake already resumes a session from a presented token
    // (websocket_endpoint.py:341-391), so all this has to do is turn the
    // ticket into a token: exchange over REST, adopt the returned handle,
    // and the handshake below carries it. No new server mechanism.
    //
    // Doing it HERE rather than inside RESTTransport is the whole point —
    // the REST-only version did nothing on DCCS, which connects over
    // WebSocket.
    //
    // Guarded on a ticket EXISTING so the no-ticket path — the common one,
    // and every pre-existing caller — opens the socket in the same tick it
    // always did. An unconditional `await` here yields a microtask even when
    // it has nothing to do, which is enough to break any caller that reads
    // the socket synchronously after connect().
    if (readTicket(this._config.entityId)) {
      await this._resumeBeforeConnect();
    }

    try {
      await this._connectWebSocket();
    } catch {
      await this._fallbackToRest();
    }
  }

  /**
   * Turn a stored resume ticket into a session token, and replay the
   * transcript. Silent on every failure — the caller then connects fresh,
   * exactly as it did before this feature.
   */
  private async _resumeBeforeConnect(): Promise<void> {
    const ticket = readTicket(this._config.entityId);
    if (!ticket) return;
    // Spend it locally first: the server burns on receipt whatever the
    // outcome, so keeping it would only retry a dead value next time.
    clearTicket(this._config.entityId);
    try {
      const res = await fetch(`${this._config.restUrl}/api/chat/session/resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticket,
          entity_id: this._config.entityId,
        }),
        credentials: "include",
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        session_id?: string;
        session_handle?: string;
        resume_ticket?: { value?: string };
        messages?: Array<{ role: string; text: string; timestamp?: number }>;
      };
      if (!data?.session_handle) return;
      // The handshake below presents this, so the WS resumes the SAME
      // session instead of minting a new one.
      this.setSessionToken(data.session_handle);
      if (data.resume_ticket?.value) {
        storeTicket(this._config.entityId, data.resume_ticket.value);
      }
      // Set BEFORE dispatching: _connectWebSocket() runs straight after this
      // method returns and reads the flag when it builds the auth frame.
      if ((data.messages ?? []).length > 0) this._replayedTranscript = true;
      for (const turn of data.messages ?? []) {
        this._dispatchMessage({
          type: turn.role === "user" ? "user_message" : "bot_message",
          payload: { text: turn.text, replayed: true },
          message_id: `replay-${turn.timestamp ?? 0}-${Math.random()
            .toString(36)
            .slice(2)}`,
          timestamp: turn.timestamp ?? 0,
          metadata: { replayed: true },
        });
      }
    } catch {
      // Offline or refused — indistinguishable to the patient, and both
      // mean "start a fresh conversation".
    }
  }

  /**
   * Disconnect from all transports and release resources.
   * After disconnect, the instance should not be reused.
   */
  disconnect(): void {
    this._disposed = true;
    this._clearReconnectTimer();
    this._stopHeartbeat();
    this._stopUpgradeCheck();

    if (this._ws) {
      this._ws.onopen = null;
      this._ws.onmessage = null;
      this._ws.onerror = null;
      this._ws.onclose = null;
      this._ws.close();
      this._ws = null;
    }

    this._rest.disconnect();
    this._messageQueue = [];
    this._setState(TransportState.DISCONNECTED);
  }

  /**
   * Send a message to the backend via the currently active transport.
   * If no transport is connected the message is queued for delivery
   * once a connection is re-established.
   */
  async send(message: ChatMessage): Promise<void> {
    if (this._state === TransportState.CONNECTED_WS && this._ws) {
      this._ws.send(JSON.stringify(message));
      return;
    }
    if (this._state === TransportState.CONNECTED_REST) {
      await this._rest.send(message);
      return;
    }
    // Queue message for delivery after reconnection (capped at 100 to prevent
    // unbounded memory growth during prolonged disconnections — M6)
    if (this._messageQueue.length >= 100) {
      this._messageQueue.shift(); // Drop oldest
    }
    this._messageQueue.push(message);
  }

  // -- WebSocket lifecycle -------------------------------------------------

  private _connectWebSocket(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = new URL(this._config.wsUrl);
      // Only entity_id in URL (non-sensitive). Session token is sent via
      // the first WS message to avoid leaking into server/proxy/CDN logs.
      url.searchParams.set("entity_id", this._config.entityId);
      // Acceptance-harness binding ID (journal 0094). Optional and
      // non-secret — safe to include in the URL alongside entity_id.
      // Present only when the widget is mounted under
      // /admin/acceptance/live; absent for production end-user mounts.
      if (this._config.harnessSessionId) {
        url.searchParams.set(
          "harness_session_id",
          this._config.harnessSessionId,
        );
      }

      let settled = false;

      try {
        this._ws = new WebSocket(url.toString());
      } catch (err) {
        reject(err);
        return;
      }

      this._ws.onopen = () => {
        // Send auth handshake as the first WS message (not in URL)
        const authMsg = JSON.stringify({
          session_token: this._config.sessionToken ?? "",
          // REQ-A-020 / #880 — tell the server we already hold the
          // transcript, so it skips replaying the bot frames that are in it.
          // Without this the handshake repaints every bot answer the resume
          // exchange just rendered (observed live 2026-08-24).
          transcript_replayed: this._replayedTranscript,
        });
        this._ws?.send(authMsg);

        // Don't resolve yet — wait for server welcome/error in onmessage
      };

      this._ws.onmessage = (event: MessageEvent) => {
        if (!settled) {
          // First message is the auth response (welcome or error)
          try {
            const msg = JSON.parse(String(event.data));
            // GH #753 — the welcome frame carries the opaque session
            // token at payload.session_token when the server minted a
            // persistent session for an anonymous mount. Capture it
            // (in memory only — never localStorage, per PDPA/M07-T05)
            // so every reconnect handshake presents it and the server
            // RESUMES the session: without this, each reconnect minted
            // a fresh session and any reply buffered for the old one
            // was silently lost (the DCCS "typing dots forever" trace,
            // session bdda7d15 → 023ea15b).
            const welcomeToken = (
              (msg.payload ?? {}) as { session_token?: unknown }
            ).session_token;
            if (typeof welcomeToken === "string" && welcomeToken) {
              this.setSessionToken(welcomeToken);
            }
            // REQ-A-020 / #880 — the welcome frame also carries a rolling
            // resume ticket. Storing it here is what makes the fix work on
            // the WEBSOCKET transport, which is what DCCS actually uses;
            // without it the ticket existed only on the REST fallback and
            // the fix did nothing on the surface the issue is about.
            const welcomeTicket = (
              (msg.payload ?? {}) as { resume_ticket?: { value?: string } }
            ).resume_ticket?.value;
            if (typeof welcomeTicket === "string" && welcomeTicket) {
              storeTicket(this._config.entityId, welcomeTicket);
            }
            if (msg.type === "error") {
              settled = true;
              // Server emits the human message at payload.text per
              // protocol.py WSMessage.error. The pre-2026-05-05
              // shape used payload.message; keep that as a fallback
              // so a widget upgraded ahead of the server can still
              // surface the error string. msg.message (top-level) is
              // a final legacy fallback that has never been wire
              // shape, retained only to avoid silently dropping any
              // server variant.
              const errPayload = (msg.payload ?? {}) as {
                text?: string;
                message?: string;
              };
              reject(
                new Error(
                  errPayload.text ??
                    errPayload.message ??
                    msg.message ??
                    "Auth handshake rejected",
                ),
              );
              return;
            }
          } catch {
            // Non-JSON first message — treat as successful
          }
          settled = true;
          this._reconnectAttempt = 0;
          this._setState(TransportState.CONNECTED_WS);
          this._startHeartbeat();
          this._flushQueue();
          resolve();
          return;
        }
        this._handleWsMessage(event);
      };

      this._ws.onerror = () => {
        if (!settled) {
          settled = true;
          reject(new Error("WebSocket connection failed"));
        }
      };

      this._ws.onclose = () => {
        if (!settled) {
          settled = true;
          reject(new Error("WebSocket closed before open"));
          return;
        }
        // Only attempt reconnect if we haven't been intentionally disconnected
        if (!this._disposed) {
          this._handleWsClose();
        }
      };
    });
  }

  private _handleWsMessage(event: MessageEvent): void {
    let parsed: ChatMessage;
    try {
      parsed = JSON.parse(String(event.data)) as ChatMessage;
    } catch {
      return; // Ignore malformed messages
    }

    // Respond to server ping with pong
    if (parsed.type === "ping") {
      this._ws?.send(
        JSON.stringify({
          type: "pong",
          payload: {},
          message_id: parsed.message_id,
          timestamp: Date.now(),
        }),
      );
      return;
    }

    this._dispatchMessage(parsed);
  }

  private _handleWsClose(): void {
    this._stopHeartbeat();
    this._ws = null;

    if (this._reconnectAttempt < this._config.reconnectMaxAttempts) {
      this._setState(TransportState.RECONNECTING);
      this._scheduleReconnect();
    } else {
      // Exhausted reconnection attempts — fall back to REST
      void this._fallbackToRest();
    }
  }

  // -- Reconnection --------------------------------------------------------

  private _scheduleReconnect(): void {
    this._clearReconnectTimer();
    const delay = Math.min(
      this._config.reconnectBaseDelay * Math.pow(2, this._reconnectAttempt),
      MAX_RECONNECT_DELAY,
    );
    this._reconnectAttempt++;

    this._reconnectTimer = setTimeout(() => {
      if (this._disposed) return;
      this._connectWebSocket()
        .then(() => {
          // Successfully reconnected via WS — stop REST if it was active
          this._rest.disconnect();
          this._stopUpgradeCheck();
        })
        .catch(() => {
          if (!this._disposed) {
            this._handleWsClose();
          }
        });
    }, delay);
  }

  private _clearReconnectTimer(): void {
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  // -- REST fallback -------------------------------------------------------

  private async _fallbackToRest(): Promise<void> {
    if (this._disposed) return;
    try {
      await this._rest.connect();
      this._setState(TransportState.CONNECTED_REST);
      this._flushQueue();
      this._startUpgradeCheck();
    } catch {
      this._setState(TransportState.ERROR);
    }
  }

  // -- Upgrade back to WebSocket -------------------------------------------

  /**
   * Periodically attempt to upgrade from REST back to WebSocket.
   * Uses the heartbeat interval as the check cadence.
   */
  private _startUpgradeCheck(): void {
    this._stopUpgradeCheck();
    this._upgradeTimer = setInterval(() => {
      if (this._disposed || this._state !== TransportState.CONNECTED_REST) {
        this._stopUpgradeCheck();
        return;
      }
      this._reconnectAttempt = 0;
      this._connectWebSocket()
        .then(() => {
          this._rest.disconnect();
          this._stopUpgradeCheck();
        })
        .catch(() => {
          // Stay on REST — will try again next interval
        });
    }, this._config.heartbeatInterval);
  }

  private _stopUpgradeCheck(): void {
    if (this._upgradeTimer !== null) {
      clearInterval(this._upgradeTimer);
      this._upgradeTimer = null;
    }
  }

  // -- Heartbeat -----------------------------------------------------------

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    // Application-layer keepalive: emit `pong` (NOT `ping`) on the
    // configured interval. The server's protocol allows clients to
    // send `pong` as a liveness signal; `ping` is server-only.
    // Sending `ping` causes the server's validate_client_message to
    // reject it with a "Clients cannot send 'ping' messages" error
    // frame every interval — historically the source of the
    // periodic empty grey bubbles. See:
    //   src/hana/gateway/protocol.py CLIENT_MESSAGE_TYPES
    //   src/hana/gateway/websocket_endpoint.py validate_client_message
    this._heartbeatTimer = setInterval(() => {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(
          JSON.stringify({
            type: "pong",
            payload: {},
            message_id: `hb-${Date.now()}`,
            timestamp: Date.now(),
          }),
        );
      }
    }, this._config.heartbeatInterval);
  }

  private _stopHeartbeat(): void {
    if (this._heartbeatTimer !== null) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
  }

  // -- Message dispatch & queue --------------------------------------------

  private _dispatchMessage(message: ChatMessage): void {
    for (const handler of this._messageHandlers) {
      handler(message);
    }
  }

  private _flushQueue(): void {
    const queued = [...this._messageQueue];
    this._messageQueue = [];
    for (const msg of queued) {
      void this.send(msg);
    }
  }

  // -- State management ----------------------------------------------------

  private _setState(state: TransportState): void {
    if (this._state === state) return;
    this._state = state;
    for (const handler of this._stateHandlers) {
      handler(state);
    }
  }
}
